import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ReportDataResponse, TitleOption, SaveFinalResultRequest, ReportRow,
} from './models/report.models';

@Injectable({ providedIn: 'root' })
export class ReportApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  // ── Titles ───────────────────────────────────────────────────────────────
  getTitles(): Observable<TitleOption[]> {
    return this.http.get<any[]>(`${this.base}/Report/Title`).pipe(
      map(items => items.map(t => {
        // API returns PascalCase; map to camelCase TitleOption.
        // Display name uses MediaBasedTitle (preferred) falling back to Titlename,
        // matching original C# GetTitleDropDownDetails() which sets Titlename = MediaBasedTitle.ToUpper().
        const name = ((t.MediaBasedTitle || t.Titlename || '') as string).toUpperCase();
        // WBS: truncate at first '.' + 1 char (e.g. "A.1234" → "A.1"), per original C#.
        let wbs = (t.WBSElem || '') as string;
        if (wbs.includes('.')) {
          wbs = wbs.substring(0, wbs.indexOf('.') + 2);
        }
        return {
          titleId: t.TitleId as number,
          titleName: name,
          wbsElem: wbs,
          releaseDate: t.ReleaseDate as string | undefined,
        } satisfies TitleOption;
      }))
    );
  }

  getWbsByTitle(title: string): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/WBS/${encodeURIComponent(title)}`);
  }

  setReleaseDate(titleId: number, date: string): Observable<void> {
    return this.http.get<void>(`${this.base}/Report/ReleaseDate/${date}/${titleId}`);
  }

  // ── Flash report data ────────────────────────────────────────────────────
  /** GET /Report/Flash/Title/{TitleId} — returns { GroupName: [rows] } */
  getReportData(titleId: number): Observable<ReportDataResponse> {
    return this.http.get<ReportDataResponse>(`${this.base}/Report/Flash/Title/${titleId}`);
  }

  /** GET /Report/GetGroupSchema — ordered list of group names */
  getGroupSchema(): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/Report/GetGroupSchema`);
  }

  // ── Saving ───────────────────────────────────────────────────────────────
  /** POST /Report/SaveFinalResult — save edited rows.
   *  Python FinalCommitmentSave schema uses PascalCase field names, so we map here. */
  saveFinalResult(rows: SaveFinalResultRequest[]): Observable<unknown> {
    const body = rows.map(r => ({
      Id: r.id,
      TitleId: r.titleId,
      CalcId: r.calcId,
      GLCode: r.glCode,
      Description: r.description,
      Budget: r.budget,
      CommittedInSAP: r.committedInSAP,
      NotCommittedInSAP: r.notCommittedInSAP,
      TotalCommitted: r.totalCommitted,
      Ultimate: r.ultimate,
      EstimationToComplete: r.estimationToComplete,
      Variance: r.variance,
      Comments: r.comments,
      UIEdit: r.uiEdit,
    }));
    return this.http.post(`${this.base}/Report/SaveFinalResult`, body);
  }

  // ── Alerts & dates ───────────────────────────────────────────────────────
  /** GET /Report/refreshvaluecheck — >0 means SAP refresh in progress */
  refreshValueCheck(): Observable<number> {
    return this.http.get<number>(`${this.base}/Report/refreshvaluecheck`);
  }

  getRefreshMessage(): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/GetRefreshMessage`);
  }

  /** GET /Report/Refresh/ — publishes SNS to fire ManualTrigger Lambda */
  triggerRecalculation(): Observable<void> {
    return this.http.get<void>(`${this.base}/Report/Refresh/`);
  }

  getFinalCommitmentDate(): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/GetFinalCommitmentDate`);
  }

  getFlashReportLastSavedDate(titleId: number): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/Flash/Title/LastSavedDate/${titleId}`);
  }

  getTitleSummary(titleId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/Report/TitleSummary/Title/${titleId}`);
  }

  getFlashSummaryLastSavedDate(titleId: number): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/FlashSummary/Title/LastSavedDate/${titleId}`);
  }

  getDepartments(): Observable<{ deptId: number; department: string }[]> {
    return this.http.get<{ deptId: number; department: string }[]>(`${this.base}/Report/Department`);
  }

  getDepartmentSummaryData(deptId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/Report/DepartmentSummary/Department/${deptId}`);
  }

  saveDepartmentSummary(rows: any[]): Observable<void> {
    return this.http.post<void>(`${this.base}/Report/SaveDepartmentSummary`, rows);
  }

  saveTitleSummary(rows: any[]): Observable<void> {
    return this.http.post<void>(`${this.base}/Report/SaveTitleSummary`, rows);
  }

  // ── Publish (live) ───────────────────────────────────────────────────────
  publishAll(): Observable<unknown> {
    return this.http.post<{ success: boolean; message: string }>(`${this.base}/Report/PublishAll`, {}).pipe(
      map(result => {
        if (result.success === false) {
          throw new Error(result.message);
        }
        return result;
      })
    );
  }

  publishTitleSummary(titleId: number): Observable<unknown> {
    return this.http.post(`${this.base}/Report/PublishTitleSummary/${titleId}`, {});
  }

  publishSlateSummary(): Observable<unknown> {
    return this.http.post(`${this.base}/Report/PublishSlateNMedia`, {});
  }

  publishDepartmentSummary(): Observable<unknown> {
    return this.http.post(`${this.base}/Report/PublishDepartment`, {});
  }

  // ── Publish (test) ───────────────────────────────────────────────────────
  testPublishAll(): Observable<unknown> {
    return this.http.post(`${this.base}/Report/test/PublishAll`, {});
  }

  testPublishSlateSummary(): Observable<unknown> {
    return this.http.post(`${this.base}/Report/test/publishSlateNMedia`, {});
  }

  testPublishTitle(titleId: number): Observable<unknown> {
    return this.http.post(`${this.base}/Report/test/publishTitle/${titleId}`, {});
  }

  testPublishDepartment(): Observable<unknown> {
    return this.http.post(`${this.base}/Report/test/publishDepartment`, {});
  }

  // ── View published reports ───────────────────────────────────────────────
  /** GET /View/InsertedDate — returns ["2025-10-25", ...] sorted desc */
  getInsertedDates(): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/View/InsertedDate`);
  }

  /** GET /View/InsertedMonth/{year} — returns ["10", "9", ...] */
  getInsertedMonths(year: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/View/InsertedMonth/${year}`);
  }

  /** GET /View/InsertedDay/{month}/{year} — returns ["25", "15", ...] */
  getInsertedDays(month: string, year: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/View/InsertedDay/${month}/${year}`);
  }

  /**
   * GET /View/All/{FileFormat}/{sso}/{date}
   * Matches original C# getNonAdminReportById(fileFormat, date) where
   * date = "YYYY-MM-DD" and sso = current user's SSO.
   * Returns the S3 path of the generated report (response.res).
   */
  getNonAdminReport(fileFormat: 'Pdf' | 'Excel', sso: string, date: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/All/${fileFormat}/${sso}/${date}`);
  }

  /** GET /View/GeneratePDF/{path} — returns { res: presignedUrl } */
  getGeneratedPDF(path: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/GeneratePDF/${path}`);
  }

  /** GET a generated PDF as a blob so the caller can control the download filename. */
  downloadGeneratedPDF(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  /** GET /Report/PublishValueCheck/ — returns count as string; >0 means publishing in progress */
  publishValueCheck(): Observable<string> {
    return this.http.get<string>(`${this.base}/Report/PublishValueCheck/`);
  }

  /** GET /Report/GetUserById/{sso} */
  getUserById(sso: string): Observable<any> {
    return this.http.get<any>(`${this.base}/Report/GetUserById/${sso}`);
  }

  /** GET /Report/GetTitlePermission — all title permissions */
  getTitlePermissions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/Report/GetTitlePermission`);
  }

  /** GET /View/Flash — flash report (all titles) */
  getFlashReport(): Observable<unknown> {
    return this.http.get(`${this.base}/View/Flash`);
  }

  /** GET /Report/GetSlateSummary — slate summary rows for table display */
  getSlateSummary(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/Report/GetSlateSummary`);
  }

  /** GET /View/Slate/{FileFormat} — download Excel or PDF, returns { res: presignedUrl } */
  getSlateSummaryFile(fileFormat: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/Slate/${fileFormat}`);
  }

  /** GET /View/Department/{FileFormat} — download Excel or PDF, returns { res: presignedUrl } */
  getDepartmentSummaryFile(fileFormat: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/Department/${fileFormat}`);
  }

  /** POST /Report/ImportSlateSummary — import slate rows from parsed xlsx */
  importSlateSummary(rows: any[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.base}/Report/ImportSlateSummary`, rows);
  }

  /** POST /Report/UploadSlateSummaryFile — upload raw xlsx to S3 (mirrors old aws-sdk upload) */
  uploadSlateSummaryFile(file: File): Observable<{ ok: boolean }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ ok: boolean }>(`${this.base}/Report/UploadSlateSummaryFile`, form);
  }

  /** GET /Report/GetMediaSummary — fetch media rows for table */
  getMediaSummary(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}/Report/GetMediaSummary`);
  }

  /** GET /View/Media/{FileFormat} — download Excel or PDF, returns { res: presignedUrl } */
  getMediaSummaryFile(fileFormat: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/Media/${fileFormat}`);
  }

  /** POST /Report/ImportMediaSummary — save parsed media rows to DB */
  importMediaSummary(rows: any[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.base}/Report/uploadmedia`, rows);
  }

  /** POST /Report/UploadMediaSummaryFile — upload raw xlsx to S3 */
  uploadMediaSummaryFile(file: File): Observable<{ ok: boolean }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ ok: boolean }>(`${this.base}/Report/UploadMediaSummaryFile`, form);
  }

  /** GET /View/Department — department summary */
  getDepartmentSummary(): Observable<unknown> {
    return this.http.get(`${this.base}/View/Department`);
  }

  /** GET /View/FlashReport/{TitleIds}/{FileFormat} — generate Excel/PDF, returns { res: presignedUrl } */
  getFlashReportFile(titleIds: string, fileFormat: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/FlashReport/${titleIds}/${fileFormat}`);
  }

  /** GET /View/FlashSummary/{TitleIds}/{FileFormat} — generate Flash Summary Excel/PDF, returns { res: presignedUrl } */
  getFlashSummaryFile(titleIds: string, fileFormat: string): Observable<{ res: string }> {
    return this.http.get<{ res: string }>(`${this.base}/View/FlashSummary/${titleIds}/${fileFormat}`);
  }
}
